import React from 'react';
import { ArrowLeft, Heart, Target, Award, Users, MapPin, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

function AboutUs() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-semibold">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-8 h-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-slate-900">About BrightBazaar.in</h1>
          </div>
          <p className="text-slate-600 text-lg">Empowering Dreams Through Smart Shopping</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Our Story */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Our Story</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Welcome to <strong>BrightBazaar.in</strong>, a proud initiative by <strong>BrightPlanet Ventures</strong>. 
              We are more than just an e-commerce platform – we are a movement dedicated to making quality 
              products accessible to everyone through innovative financing solutions and exceptional customer service.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              Founded with a vision to bridge the gap between aspiration and affordability, BrightBazaar.in 
              offers a unique 20-month savings plan that rewards our customers with monthly coupons, making 
              their dream purchases a reality without financial strain.
            </p>
            <p className="text-slate-700 leading-relaxed">
              Operating from the heart of Karnataka, we serve communities across Bailhongal, Gokak, Kittur, 
              and beyond, bringing the convenience of online shopping combined with the trust of local business.
            </p>
          </section>

          {/* Mission & Vision */}
          <section>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-8 h-8 text-orange-600" />
                  <h2 className="text-xl font-bold text-slate-900">Our Mission</h2>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  To revolutionize e-commerce by combining affordability with quality, enabling every 
                  household to access premium products through innovative savings plans and flexible 
                  payment options.
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Award className="w-8 h-8 text-blue-600" />
                  <h2 className="text-xl font-bold text-slate-900">Our Vision</h2>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  To become India's most trusted e-commerce platform, known for customer-centric 
                  innovation, transparent practices, and empowering families to achieve their lifestyle 
                  aspirations without compromise.
                </p>
              </div>
            </div>
          </section>

          {/* What Makes Us Different */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">What Makes Us Different</h2>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-orange-600">
                <h3 className="font-bold text-slate-900 mb-2">20-Month Savings Plan</h3>
                <p className="text-slate-700 text-sm">
                  Our signature offering allows customers to save systematically while earning monthly 
                  shopping coupons worth ₹1,000 each. Complete the plan and enjoy ₹20,000 in total benefits!
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-blue-600">
                <h3 className="font-bold text-slate-900 mb-2">Zero Interest EMI</h3>
                <p className="text-slate-700 text-sm">
                  Shop for your favorite electronics, furniture, and fashion items with 0% EMI options, 
                  making big purchases manageable and stress-free.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-green-600">
                <h3 className="font-bold text-slate-900 mb-2">Quality Assured Products</h3>
                <p className="text-slate-700 text-sm">
                  Every product is carefully curated from trusted brands and manufacturers, ensuring 
                  you receive genuine, high-quality items backed by warranties.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border-l-4 border-purple-600">
                <h3 className="font-bold text-slate-900 mb-2">Local Trust, Digital Convenience</h3>
                <p className="text-slate-700 text-sm">
                  With physical presence in multiple Karnataka locations, we combine the reliability 
                  of local business with the convenience of online shopping.
                </p>
              </div>
            </div>
          </section>

          {/* Our Categories */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">What We Offer</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                'Electronics & Appliances',
                'Home Furniture',
                'Fashion & Apparel',
                'Kitchen Essentials',
                'Home Décor',
                'Smart Devices'
              ].map((category) => (
                <div key={category} className="bg-white border border-gray-200 rounded-lg p-4 text-center hover:shadow-md transition-shadow">
                  <p className="text-slate-700 font-semibold text-sm">{category}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Our Values */}
          <section className="bg-gradient-to-r from-orange-50 to-blue-50 rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Our Core Values</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-3">
                <span className="text-orange-600 font-bold">✓</span>
                <div>
                  <strong className="text-slate-900">Customer First:</strong>
                  <span className="text-slate-700"> Your satisfaction drives everything we do</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-600 font-bold">✓</span>
                <div>
                  <strong className="text-slate-900">Transparency:</strong>
                  <span className="text-slate-700"> Clear pricing, honest communication</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-600 font-bold">✓</span>
                <div>
                  <strong className="text-slate-900">Innovation:</strong>
                  <span className="text-slate-700"> Continuously improving the shopping experience</span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-orange-600 font-bold">✓</span>
                <div>
                  <strong className="text-slate-900">Integrity:</strong>
                  <span className="text-slate-700"> Building trust through ethical practices</span>
                </div>
              </div>
            </div>
          </section>

          {/* Our Presence */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <MapPin className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-4">Our Presence</h2>
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-orange-600 text-sm mb-2">Bailhongal Headquarters</h3>
                    <p className="text-slate-700 text-sm">
                      Mulla Complex, Somwar Pethe<br />
                      Near MalaPrabha Hospital<br />
                      Bailhongal - 591102, Karnataka
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-orange-600 text-sm mb-2">Gokak Branch</h3>
                    <p className="text-slate-700 text-sm">
                      Navarang Plaza<br />
                      Opposite Masurkar Hospital, Naka No.1<br />
                      Gokak - 591307, Karnataka
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="font-bold text-orange-600 text-sm mb-2">Kittur Hub</h3>
                    <p className="text-slate-700 text-sm">
                      Kittur - 591115, Karnataka
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Team Section */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-orange-600" />
              <h2 className="text-xl font-bold text-slate-900">Our Team</h2>
            </div>
            <p className="text-slate-700 leading-relaxed">
              Behind BrightBazaar.in is a dedicated team of professionals passionate about delivering 
              exceptional service. From our customer support staff to our logistics partners, every 
              member is committed to ensuring your shopping experience is smooth, secure, and satisfying.
            </p>
          </section>

          {/* Contact Section */}
          <section className="border-t pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Get In Touch</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We'd love to hear from you! Whether you have questions, feedback, or need assistance, 
              our team is here to help.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-orange-600" />
                <span className="text-slate-700"><strong>Phone:</strong> +91 73532 97211</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-orange-600" />
                <span className="text-slate-700"><strong>Email:</strong> info@brightplanetventures.com</span>
              </div>
              <div className="flex items-start gap-3 mt-4">
                <div className="text-slate-700 text-sm">
                  <strong>GST Number:</strong> 29AAKCD7090A1ZL
                </div>
              </div>
            </div>
          </section>

          {/* Closing Statement */}
          <section className="bg-orange-50 rounded-lg p-6 text-center">
            <p className="text-slate-700 leading-relaxed italic">
              "At BrightBazaar.in, we believe everyone deserves access to quality products without 
              compromising their financial well-being. Join thousands of satisfied customers who have 
              made their dreams a reality with us."
            </p>
            <Link 
              to="/" 
              className="inline-block mt-4 bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Start Shopping Now
            </Link>
          </section>

        </div>
      </div>
    </div>
  );
}

export default AboutUs;
